import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FaSearch, FaPrint, FaInfoCircle } from "react-icons/fa";
import API_BASE_URL from "../../../components/apiconfig/api-config";
import NoRecordFound from "../../../components/common/no-record-found";
import SearchableDropdown from "../../../components/common/SearchableDropdown";
import { FINANCIAL_YEAR_OPTIONS } from "../../../components/common/dateUtils";

// Date formatting function to ensure dd/mm/yyyy format
const formatDate = (dateString) => {
  if (!dateString) return "-";

  // If it's already in dd/mm/yyyy format, return as is
  if (dateString.includes("/")) {
    return dateString;
  }

  // If it's in "dd Month yyyy" format, convert it
  if (dateString.includes(" ")) {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }
  }

  return dateString;
};

// Indian number formatting function (lakhs, crores)
const formatIndianNumber = (number) => {
  if (!number || isNaN(number)) return "-";

  const num = parseFloat(number);
  if (num === 0) return "0.00";

  // Convert to string with 2 decimal places
  const numStr = num.toFixed(2);

  // Split into integer and decimal parts
  const [integerPart, decimalPart] = numStr.split(".");

  // Format integer part with Indian numbering system
  let formattedInteger = "";
  const len = integerPart.length;

  if (len <= 3) {
    formattedInteger = integerPart;
  } else if (len <= 5) {
    // For numbers up to 99,999 (lakhs)
    formattedInteger =
      integerPart.slice(0, len - 3) + "," + integerPart.slice(len - 3);
  } else {
    // For numbers 1,00,000 and above (crores)
    // Proper Indian numbering system: 1,00,00,000 (1 crore)
    let remaining = integerPart;
    const parts = [];

    // Extract last 3 digits (ones)
    if (remaining.length > 3) {
      parts.unshift(remaining.slice(-3));
      remaining = remaining.slice(0, -3);
    } else {
      parts.unshift(remaining);
      remaining = "";
    }

    // Extract next 2 digits (thousands)
    if (remaining.length > 2) {
      parts.unshift(remaining.slice(-2));
      remaining = remaining.slice(0, -2);
    } else if (remaining.length > 0) {
      parts.unshift(remaining);
      remaining = "";
    }

    // Extract next 2 digits (lakhs)
    if (remaining.length > 2) {
      parts.unshift(remaining.slice(-2));
      remaining = remaining.slice(0, -2);
    } else if (remaining.length > 0) {
      parts.unshift(remaining);
      remaining = "";
    }

    // Add remaining digits as crores
    while (remaining.length > 0) {
      if (remaining.length > 2) {
        parts.unshift(remaining.slice(-2));
        remaining = remaining.slice(0, -2);
      } else {
        parts.unshift(remaining);
        remaining = "";
      }
    }

    formattedInteger = parts.join(",");
  }

  return `${formattedInteger}.${decimalPart}`;
};

const TYPE_OF_PURCHASE_OPTIONS = ["Origin", "High Sea", "Domestic"];

const ImportSheetSummary = () => {
  const [reportData, setReportData] = useState({
    combinedData: [],
    totals: {},
  });
  const [states, setStates] = useState([]);
  const [commodities, setCommodities] = useState([]);

  // Convert arrays to objects for SearchableDropdown
  const stateOptions = useMemo(
    () => states.map((state) => ({ id: state, name: state })),
    [states]
  );

  const commodityOptions = useMemo(
    () => commodities.map((commodity) => ({ id: commodity, name: commodity })),
    [commodities]
  );
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    financialYear: "",
    state: "",
    commodity: "",
    showWithoutVesselDeparture: false, // Add this new filter
    contractDateFrom: "",
    contractDateTo: "",
    typeOfPurchase: [],
    currency: "",
    hedgeExecuted: ""
  });
  const isFySelected = Boolean(filters.financialYear);
  const [groupedData, setGroupedData] = useState([]);
  const handleContractDateFromChange = (value) => {
  setFilters(prev => ({ ...prev, contractDateFrom: value }));
};

const handleContractDateToChange = (value) => {
  setFilters(prev => ({ ...prev, contractDateTo: value }));
};
const toggleTypeOfPurchase = (value) => {
  setFilters((prev) => {
    const exists = prev.typeOfPurchase.includes(value);
    const updated = exists
      ? prev.typeOfPurchase.filter((item) => item !== value)
      : [...prev.typeOfPurchase, value];
    return { ...prev, typeOfPurchase: updated };
  });
};
const handleVesselDepartureFilter = () => {
  setFilters((prev) => ({ 
    ...prev, 
    showWithoutVesselDeparture: !prev.showWithoutVesselDeparture 
  }));
};

 const fetchReportData = useCallback(async () => {
  setLoading(true);
  try {
    const queryParams = new URLSearchParams();
    
    // Map frontend filter names to backend parameter names
    const filterMapping = {
      financialYear: 'financial_year',
      state: 'state',
      commodity: 'commodity',
      contractDateFrom: 'contract_date_from',  // Map to backend name
      contractDateTo: 'contract_date_to',      // Map to backend name
      showWithoutVesselDeparture: 'showWithoutVesselDeparture',
      typeOfPurchase: 'type_of_purchase',
      currency: 'currency',
      hedgeExecuted: 'hedge_executed'
    };

    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (!filterMapping[key]) {
        return;
      }

      if (Array.isArray(value)) {
        if (value.length > 0) {
          value.forEach((item) => {
            if (item) {
              queryParams.append(filterMapping[key], item);
            }
          });
        }
      } else if (value) {
        // Use mapped parameter name for backend
        queryParams.append(filterMapping[key], value);
      }
    });

    console.log('Sending query params:', queryParams.toString()); // Debug log

    const response = await fetch(
      `${API_BASE_URL}/api/report/import-sheet-summary-report?${queryParams}`,
      {
        credentials: "include",
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    setReportData(data);
    if (data.states) {
      setStates(data.states);
    }
    if (data.commodities) {
      setCommodities(data.commodities);
    }
  } catch (error) {
    console.error("Error fetching report data:", error);
    setReportData({
      combinedData: [],
      totals: {},
    });
  } finally {
    setLoading(false);
  }
}, [filters]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  // Group data by commodity and state
  // Update the useEffect that groups data
// Update the useEffect that groups data
// Update the useEffect that groups data - ADD CURRENCY TO KEY
useEffect(() => {
  const combinedData = reportData?.combinedData || [];
  
  const filteredData = filters.showWithoutVesselDeparture
    ? combinedData.filter(row => 
        !row.vessel_departure_pod || 
        row.vessel_departure_pod.trim() === '' || 
        row.vessel_departure_pod === '-' || 
        row.vessel_departure_pod === null
      )
    : combinedData;

  const groupedMap = new Map();

  filteredData.forEach((row) => {
    const commodity = row.commodity || "Unknown Commodity";
    const state = row.state || "Unknown State";
    const currency = row.currency || "USD"; // Get currency from row
    
    // KEY CHANGE: Include currency in grouping key
    const key = `${commodity}|${state}|${currency}`;

    if (!groupedMap.has(key)) {
      groupedMap.set(key, {
        commodity,
        state,
        currency, // Store currency separately
        data: [],
        purchaseTotals: {
          total_qty_mt: 0,
          total_amount_usd: 0,
          total_amount_inr: 0,
        },
        salesTotals: {
          total_qty_mt: 0,
          total_amount: 0,
        },
        countedPurchaseContracts: new Set(),
        countedSalesContracts: new Set(),
      });
    }

    const group = groupedMap.get(key);
    group.data.push(row);

    // Purchase tracking (unique by purchase_contract_no)
    if (row.contracted_qty_mt && !group.countedPurchaseContracts.has(row.purchase_contract_no)) {
      group.purchaseTotals.total_qty_mt += parseFloat(row.contracted_qty_mt) || 0;
      group.purchaseTotals.total_amount_usd += parseFloat(row.contract_amount_usd) || 0;
      
      const contractAmountUsd = parseFloat(row.contract_amount_usd) || 0;
      const hedgingRate = parseFloat(row.hedging_rate) || 0;
      group.purchaseTotals.total_amount_inr += contractAmountUsd * hedgingRate;
      
      group.countedPurchaseContracts.add(row.purchase_contract_no);
    }

    // Sales tracking
    if (row.sale_quantity_mt && row.purchase_contract_id) {
      const salesKey = `${row.purchase_contract_id}`;
      if (!group.countedSalesContracts.has(salesKey)) {
        group.salesTotals.total_qty_mt += parseFloat(row.sale_quantity_mt) || 0;
        group.salesTotals.total_amount += parseFloat(row.contracted_amount) || 0;
        group.countedSalesContracts.add(salesKey);
      }
    }
  });

  // Convert to array and format data
  const groupedArray = Array.from(groupedMap.values()).map((group) => {
    const purchaseContractGroups = new Map();
    const processedSales = new Set();

    group.data.forEach((row) => {
      const purchaseContractNo = row.purchase_contract_no || "no_contract";
      
      if (!purchaseContractGroups.has(purchaseContractNo)) {
        purchaseContractGroups.set(purchaseContractNo, {
          purchaseData: row,
          salesData: [],
        });
      }

      if (row.sale_quantity_mt && row.purchase_contract_id) {
        const salesKey = `${row.purchase_contract_id}`;
        if (!processedSales.has(salesKey)) {
          const contractGroup = purchaseContractGroups.get(purchaseContractNo);
          contractGroup.salesData.push(row);
          processedSales.add(salesKey);
        }
      }
    });

    // Format data for display - NO NEED TO SEPARATE USD/INR
    // Each group already represents single currency
    const formattedData = [];
    
    purchaseContractGroups.forEach((contractGroup, purchaseContractNo) => {
      const { purchaseData, salesData } = contractGroup;

      if (salesData.length > 0) {
        salesData.forEach((salesRow, index) => {
          formattedData.push({
            ...purchaseData,
            ...salesRow,
            isFirstRow: index === 0,
            purchase_contract_no: purchaseData.purchase_contract_no,
            currency: purchaseData.currency || 'USD',
            type_of_purchase: purchaseData.type_of_purchase,
          });
        });
      } else {
        formattedData.push({
          ...purchaseData,
          isFirstRow: true,
          salesData: [],
          currency: purchaseData.currency || 'USD',
          type_of_purchase: purchaseData.type_of_purchase,
        });
      }
    });

    // Calculate averages
    if (group.purchaseTotals.total_qty_mt > 0) {
      group.purchaseTotals.avg_price_usd_mt = 
        group.purchaseTotals.total_amount_usd / group.purchaseTotals.total_qty_mt;
    }
    
    if (group.salesTotals.total_qty_mt > 0) {
      group.salesTotals.avg_price_pmt = 
        group.salesTotals.total_amount / group.salesTotals.total_qty_mt;
    }

    // Clean up temporary sets
    delete group.countedPurchaseContracts;
    delete group.countedSalesContracts;

    return {
      ...group,
      data: formattedData,
    };
  });

  // Separate USD and INR groups
  const usdGroups = [];
  const inrGroups = [];
  const usdTotals = {
    total_qty_mt: 0,
    total_amount_usd: 0,
    total_amount_inr: 0,
    total_sale_qty: 0,
    total_sale_amount: 0,
  };
  const inrTotals = {
    total_qty_mt: 0,
    total_amount_usd: 0,
    total_amount_inr: 0,
    total_sale_qty: 0,
    total_sale_amount: 0,
  };

  groupedArray.forEach((group) => {
    if (group.currency === 'USD') {
      usdGroups.push(group);
      usdTotals.total_qty_mt += group.purchaseTotals.total_qty_mt || 0;
      usdTotals.total_amount_usd += group.purchaseTotals.total_amount_usd || 0;
      usdTotals.total_amount_inr += group.purchaseTotals.total_amount_inr || 0;
      usdTotals.total_sale_qty += group.salesTotals.total_qty_mt || 0;
      usdTotals.total_sale_amount += group.salesTotals.total_amount || 0;
    } else {
      inrGroups.push(group);
      inrTotals.total_qty_mt += group.purchaseTotals.total_qty_mt || 0;
      inrTotals.total_amount_usd += group.purchaseTotals.total_amount_usd || 0;
      inrTotals.total_amount_inr += group.purchaseTotals.total_amount_inr || 0;
      inrTotals.total_sale_qty += group.salesTotals.total_qty_mt || 0;
      inrTotals.total_sale_amount += group.salesTotals.total_amount || 0;
    }
  });

  // Sort USD groups: State -> Commodity
  usdGroups.sort((a, b) => {
    const stateCompare = (a.state || "").toLowerCase().localeCompare((b.state || "").toLowerCase());
    if (stateCompare !== 0) return stateCompare;
    return (a.commodity || "").toLowerCase().localeCompare((b.commodity || "").toLowerCase());
  });

  // Sort INR groups: State -> Commodity
  inrGroups.sort((a, b) => {
    const stateCompare = (a.state || "").toLowerCase().localeCompare((b.state || "").toLowerCase());
    if (stateCompare !== 0) return stateCompare;
    return (a.commodity || "").toLowerCase().localeCompare((b.commodity || "").toLowerCase());
  });

  // Create final array: All USD groups -> USD Grand Total -> All INR groups -> INR Grand Total
  const finalGroupedArray = [];
  
  // Add all USD groups
  finalGroupedArray.push(...usdGroups);
  
  // Add USD grand total if there are USD groups
  if (usdGroups.length > 0) {
    finalGroupedArray.push({
      isCurrencyTotal: true,
      currency: 'USD',
      currencyTotals: usdTotals,
    });
  }
  
  // Add all INR groups
  finalGroupedArray.push(...inrGroups);
  
  // Add INR grand total if there are INR groups
  if (inrGroups.length > 0) {
    finalGroupedArray.push({
      isCurrencyTotal: true,
      currency: 'INR',
      currencyTotals: inrTotals,
    });
  }

  setGroupedData(finalGroupedArray);
}, [reportData, filters.showWithoutVesselDeparture]);


  const handleFinancialYearChange = (value) => {
    setFilters((prev) => ({
      ...prev,
      financialYear: value
    }));
  };

  const handleCommodityChange = (value) => {
    setFilters((prev) => ({ ...prev, commodity: value }));
  };

  const handleStateChange = (value) => {
    setFilters((prev) => ({ ...prev, state: value }));
  };

  const handleSearch = () => {
    fetchReportData();
  };

  

  const handlePrint = () => {
  const printWindow = window.open("", "_blank");
  
  const filterInfo = [];
  if (filters.financialYear) filterInfo.push(`Financial Year: ${filters.financialYear}`);
  if (filters.commodity) filterInfo.push(`Commodity: ${filters.commodity}`);
  if (filters.state) filterInfo.push(`State: ${filters.state}`);
  if (filters.currency) filterInfo.push(`Currency: ${filters.currency}`);
  if (filters.hedgeExecuted) filterInfo.push(`Hedge Executed: ${filters.hedgeExecuted}`);

  let printContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Statewise Commodity Report</title>
        <style>
          @media print {
            body {
              margin: 0;
              padding: 20px;
              font-family: Arial, sans-serif;
            }
            .print-header {
              text-align: center;
              margin-bottom: 20px;
              border-bottom: 2px solid #333;
              padding-bottom: 10px;
            }
            .print-title {
              font-size: 24px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            .print-subtitle {
              font-size: 14px;
              color: #666;
              margin-bottom: 10px;
            }
            .print-filters {
              font-size: 12px;
              color: #666;
              margin-bottom: 20px;
            }
            .print-date {
              font-size: 12px;
              color: #666;
              margin-bottom: 20px;
            }
            .group-header {
              background-color: #f8f9fa;
              padding: 8px;
              margin: 15px 0 10px 0;
              border-left: 4px solid #0488B6;
              font-weight: bold;
            }
            .data-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
              font-size: 10px;
            }
            .data-table th {
              background-color: #e9ecef;
              border: 1px solid #dee2e6;
              padding: 6px;
              text-align: left;
              font-weight: bold;
            }
            .data-table td {
              border: 1px solid #dee2e6;
              padding: 4px;
            }
            .data-table .text-right {
              text-align: right;
            }
            .data-table .text-center {
              text-align: center;
            }
            .totals-row {
              background-color: #f8f9fa;
              font-weight: bold;
            }
            @page {
              size: A4 landscape;
              margin: 1cm;
            }
          }
        </style>
      </head>
      <body>
        <div class="print-header">
          <div class="print-title">Import Sheet Summary As On ${new Date().toLocaleDateString("en-IN")}</div>
        </div>
  `;

  if (groupedData && Array.isArray(groupedData)) {
    groupedData.forEach((group, groupIndex) => {
      // Handle currency total rows
      if (group.isCurrencyTotal) {
        printContent += `
          <div style="margin-top:15px; padding:15px; border:2px solid #3b82f6; background-color:#dbeafe; border-radius:8px;">
            <h3 style="font-size:18px; font-weight:bold; margin-bottom:10px; text-align:center; color:#1e40af;">
              ${group.currency} GRAND TOTAL
            </h3>
            <table style="width:100%; border-collapse:collapse; font-size:14px;">
              <tr>
                <th style="padding:8px; border:1px solid #ccc; text-align:left;">Contracted Qty (MT)</th>
                <th style="padding:8px; border:1px solid #ccc; text-align:left;">Contract Amount (${group.currency === 'INR' ? '₹' : '$'})</th>
                <th style="padding:8px; border:1px solid #ccc; text-align:left;">Contract Amount (₹)</th>
                <th style="padding:8px; border:1px solid #ccc; text-align:left;">Sale Qty (MT)</th>
                <th style="padding:8px; border:1px solid #ccc; text-align:left;">Sale Amount (₹)</th>
              </tr>
              <tr>
                <td style="padding:8px; border:1px solid #ccc; text-align:right; font-weight:bold;">
                  ${formatIndianNumber(group.currencyTotals.total_qty_mt)}
                </td>
                <td style="padding:8px; border:1px solid #ccc; text-align:right; font-weight:bold;">
                  ${group.currency === 'INR'
                    ? '₹' + formatIndianNumber(group.currencyTotals.total_amount_inr)
                    : '$' + formatIndianNumber(group.currencyTotals.total_amount_usd)}
                </td>
                <td style="padding:8px; border:1px solid #ccc; text-align:right; font-weight:bold;">
                  ${formatIndianNumber(group.currencyTotals.total_amount_inr)}
                </td>
                <td style="padding:8px; border:1px solid #ccc; text-align:right; font-weight:bold;">
                  ${formatIndianNumber(group.currencyTotals.total_sale_qty)}
                </td>
                <td style="padding:8px; border:1px solid #ccc; text-align:right; font-weight:bold;">
                 ₹${formatIndianNumber(group.currencyTotals.total_sale_amount)}
                </td>
              </tr>
            </table>
          </div>
        `;
        return;
      }

      // Regular group display
      if (!group.data || !Array.isArray(group.data)) {
        return;
      }

      printContent += `
        <div class="group-header">
          Commodity: ${group.commodity || ''} | State: ${group.state || ''} | Currency: ${group.currency || 'USD'}
        </div>
        <table class="data-table">
          <thead>
            <tr>
              <th>Sl. No</th>
              <th>Purchase Contract No</th>
              <th>Contract Date</th>
              <th>Seller Name</th>
              <th>Shipment Period</th>
              <th>Vessel Name</th>
              <th>Port of Loading</th>
              <th>Port of Discharge</th>
              <th class="text-right">Contracted Qty MT</th>
              <th>Type of Purchase</th>
              <th>Currency</th>
              <th class="text-right">Price USD/MT</th>
              <th class="text-right">Contract Amount (${group.currency === 'INR' ? '₹' : '$'})</th>
              <th class="text-right">Exchange Rate</th>
              <th class="text-center">Hedge Executed</th>
              <th class="text-right">Contract Amount INR</th>
              <th>Vessel Departure</th>
              <th>ETA of Vessel</th>
              <th>Date of Sale</th>
              <th>Name of Party</th>
              <th class="text-right">Sale Qty MT</th>
              <th class="text-right">Price PMT</th>
              <th class="text-right">Sale Amount</th>
            </tr>
          </thead>
          <tbody>
      `;

    group.data.forEach((row) => {
      // Handle Subtotal Row
      if (row.isSubtotalRow) {
         const avgPrice = row.salesSubtotal.total_qty_mt > 0 
          ? row.salesSubtotal.total_amount / row.salesSubtotal.total_qty_mt 
          : 0;
        printContent += `
          <tr style="background-color: #dbeafe; font-weight: bold; border-top: 2px solid #3b82f6;">
            <td colspan="20" style="padding:6px; border:1px solid #ccc; text-align:right;">
              <span style="margin-right:16px;">Subtotal for ${row.purchase_contract_no}:</span>
            </td>
            <td style="padding:6px; border:1px solid #ccc; text-align:right;">
              ${formatIndianNumber(row.salesSubtotal.total_qty_mt)}
            </td>
              <td style="padding:6px; border:1px solid #ccc; text-align:right;">
              ${avgPrice > 0 ? formatIndianNumber(avgPrice) : "-"}
            </td>
            <td style="padding:6px; border:1px solid #ccc; text-align:right;">
              ${formatIndianNumber(row.salesSubtotal.total_amount)}
            </td>
          </tr>
        `;
        return;
      }

      // Handle Regular Data Row
      const purchaseContractNo = row.isFirstRow ? (row.purchase_contract_no || "-") : "---";
      const contractDate = row.isFirstRow ? formatDate(row.contract_date) : "---";
      const sellerName = row.isFirstRow ? (row.seller_name || "-") : "---";
      const shipmentPeriod = row.isFirstRow ? formatDate(row.shipment_period) : "---";
      const vesselName = row.isFirstRow ? (row.vessel_name || "-") : "---";
      const portOfLoading = row.isFirstRow ? (row.port_of_loading || "-") : "---";
      const portOfDischarge = row.isFirstRow ? (row.port_of_discharge || "-") : "---";
      
      const contractedQty = row.isFirstRow
        ? row.contracted_qty_mt
          ? formatIndianNumber(row.contracted_qty_mt)
          : "-"
        : "---";
        
      const currency = row.currency || group.currency || "USD";
      const priceUsd = row.isFirstRow
        ? row.price_usd_mt
          ? (currency === 'INR' ? '₹' : '$') + formatIndianNumber(row.price_usd_mt)
          : "-"
        : "---";
        
      const contractAmount = row.isFirstRow
        ? (() => {
            if (!row.contract_amount_usd) return "-";
            if (currency === 'INR') {
              const contractAmountUsd = parseFloat(row.contract_amount_usd) || 0;
              const hedgingRate = parseFloat(row.hedging_rate) || 0;
              const contractAmountInr = contractAmountUsd * hedgingRate;
              return contractAmountInr > 0 ? '₹' + formatIndianNumber(contractAmountInr) : "-";
            } else {
              return '$' + formatIndianNumber(row.contract_amount_usd);
            }
          })()
        : "---";
        
      const hedgingRate = row.isFirstRow
        ? (currency === 'INR' ? "1.00" : (row.hedging_rate ? formatIndianNumber(row.hedging_rate) : "-"))
        : "---";
        
      const contractAmountInr = row.isFirstRow
        ? (() => {
            const contractAmountUsd = parseFloat(row.contract_amount_usd) || 0;
            const hedgingRate = parseFloat(row.hedging_rate) || 0;
            const calculatedInr = contractAmountUsd * hedgingRate;
            return calculatedInr > 0 ? formatIndianNumber(calculatedInr) : "-";
          })()
        : "---";
        
      const vesselDeparture = row.isFirstRow ? (row.vessel_departure_pod || "-") : "---";
      const etaOfVessel = row.isFirstRow ? (row.eta_of_vessel || "-") : "---";

      printContent += `
        <tr>
          <td>${row.sl_no || "-"}</td>
          <td>${purchaseContractNo}</td>
          <td>${contractDate}</td>
          <td>${sellerName}</td>
          <td>${shipmentPeriod}</td>
          <td>${vesselName}</td>
          <td>${portOfLoading}</td>
          <td>${portOfDischarge}</td>
          <td class="text-right">${contractedQty}</td>
          <td>${row.isFirstRow ? (row.type_of_purchase || "-") : "---"}</td>
          <td>${row.isFirstRow ? (row.currency || "USD") : "---"}</td>
          <td class="text-right">${priceUsd}</td>
          <td class="text-right">${contractAmount}</td>
          <td class="text-right">${hedgingRate}</td>
          <td class="text-center">${row.isFirstRow ? (row.hedge_executed || "-") : "---"}</td>
          <td class="text-right">${contractAmountInr}</td>
          <td>${vesselDeparture}</td>
          <td>${etaOfVessel}</td>
          <td>-</td>
          <td>-</td>
          <td class="text-right">${row.sale_quantity_mt ? formatIndianNumber(row.sale_quantity_mt) : "-"}</td>
          <td class="text-right">${row.price_pmt ? formatIndianNumber(row.price_pmt) : "-"}</td>
          <td class="text-right">${row.contracted_amount ? formatIndianNumber(row.contracted_amount) : "-"}</td>
        </tr>
      `;
    });

      // Add totals row
      printContent += `
            <tr class="totals-row">
              <td colspan="9">TOTAL</td>
              <td class="text-right">${formatIndianNumber(group.purchaseTotals?.total_qty_mt || 0)}</td>
              <td>-</td>
              <td>-</td>
              <td class="text-right">${formatIndianNumber(group.purchaseTotals?.avg_price_usd_mt || 0)}</td>
              <td class="text-right">${group.currency === 'INR'
                ? '₹' + formatIndianNumber(group.purchaseTotals?.total_amount_inr || 0)
                : '$' + formatIndianNumber(group.purchaseTotals?.total_amount_usd || 0)}</td>
              <td class="text-right">${group.currency === 'INR' ? "1.00" : formatIndianNumber(
                (group.purchaseTotals?.total_amount_inr || 0) /
                  (group.purchaseTotals?.total_amount_usd || 1)
              )}</td>
              <td class="text-center">-</td>
              <td class="text-right">₹${formatIndianNumber(group.purchaseTotals?.total_amount_inr || 0)}</td>
              <td colspan="2"></td>
              <td colspan="1"></td>
              <td class="text-right">${formatIndianNumber(group.salesTotals?.total_qty_mt || 0)}</td>
              <td class="text-right">${formatIndianNumber(group.salesTotals?.avg_price_pmt || 0)}</td>
              <td class="text-right">${formatIndianNumber(group.salesTotals?.total_amount || 0)}</td>
            </tr>
          </tbody>
        </table>
      `;
    });
  }

  printContent += `
      </body>
    </html>
  `;

  printWindow.document.write(printContent);
  printWindow.document.close();

  printWindow.onload = function () {
    printWindow.print();
    printWindow.close();
  };
};
// Reset all filters function
const handleResetFilters = () => {
  setFilters({
    financialYear: "",
    state: "",
    commodity: "",
    showWithoutVesselDeparture: false,
    contractDateFrom: "",
    contractDateTo: "",
    typeOfPurchase: [],
    currency: "",
    hedgeExecuted: ""
  });
};
const handleCurrencyChange = (value) => {
  setFilters((prev) => ({ ...prev, currency: value }));
};
const handleHedgeExecutedChange = (value) => {
  setFilters((prev) => ({ ...prev, hedgeExecuted: value }));
};

  return (
    <div className="max-w-full mx-auto p-3 sm:p-6 bg-white border border-gray-100">
      {/* Header Section - Mobile Responsive */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-3">
        <div>
          <h2 className="text-lg sm:text-xl font-bold text-gray-800">Import Sheet Summary</h2>
          <p className="text-xs text-gray-500">Data grouped by commodity and state</p>
        </div>
        <div>
          <button
            onClick={handlePrint}
            className="px-3 py-1.5 text-xs sm:text-sm border border-gray-300 rounded hover:bg-gray-50 flex items-center justify-center transition-colors shadow-sm bg-white"
          >
            <FaPrint className="mr-1.5" />
            <span>Print</span>
          </button>
        </div>
      </div>

      {/* Filters - Compact & Organized */}
      <div className="bg-gray-50/90 border border-gray-200 rounded p-2.5 sm:p-3 mb-4 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2 sm:gap-2.5">
          {/* Financial Year Filter */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Financial Year
            </label>
            <SearchableDropdown
              options={FINANCIAL_YEAR_OPTIONS}
              value={filters.financialYear}
              onChange={handleFinancialYearChange}
              placeholder="Select FY"
              displayKey="name"
              valueKey="id"
              className="w-full font-medium"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Commodity
            </label>
            <SearchableDropdown
              options={commodityOptions}
              value={filters.commodity}
              disabled={isFySelected}
              onChange={handleCommodityChange}
              placeholder="Select commodity"
              displayKey="name"
              valueKey="id"
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              State
            </label>
            <SearchableDropdown
              options={stateOptions}
              value={filters.state}
              disabled={isFySelected}
              onChange={handleStateChange}
              placeholder="Select state"
              displayKey="name"
              valueKey="id"
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Currency
            </label>
            <SearchableDropdown
              options={[
                { id: 'USD', name: 'USD' },
                { id: 'INR', name: 'INR' }
              ]}
              value={filters.currency}
              disabled={isFySelected}
              onChange={handleCurrencyChange}
              placeholder="Select Currency"
              displayKey="name"
              valueKey="id"
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Hedge Executed
            </label>
            <SearchableDropdown
              options={[
                { id: 'Yes', name: 'Yes' },
                { id: 'No', name: 'No' }
              ]}
              value={filters.hedgeExecuted}
              disabled={isFySelected}
              onChange={handleHedgeExecutedChange}
              placeholder="Select Hedge"
              displayKey="name"
              valueKey="id"
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Date From
            </label>
            <input
              type="date"
              value={filters.contractDateFrom}
              disabled={isFySelected}
              onChange={(e) => handleContractDateFromChange(e.target.value)}
              className={`w-full h-[34px] px-2 py-1 text-xs sm:text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#0488B6] focus:border-[#0488B6] ${
                isFySelected ? 'bg-gray-100 cursor-not-allowed text-gray-400' : 'bg-white'
              }`}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Date To
            </label>
            <input
              type="date"
              value={filters.contractDateTo}
              disabled={isFySelected}
              onChange={(e) => handleContractDateToChange(e.target.value)}
              className={`w-full h-[34px] px-2 py-1 text-xs sm:text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#0488B6] focus:border-[#0488B6] ${
                isFySelected ? 'bg-gray-100 cursor-not-allowed text-gray-400' : 'bg-white'
              }`}
            />
          </div>
        </div>
        
        {/* Sub-Filters & Action Buttons Bar */}
        <div className="mt-2.5 pt-2 border-t border-gray-200 flex flex-col md:flex-row md:items-center justify-between gap-2">
          {/* Left: Type of Purchase Segment Control & No Vessel Departure */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">
                Type of Purchase:
              </span>
              <div className="inline-flex rounded shadow-sm" role="group">
                {TYPE_OF_PURCHASE_OPTIONS.map((option, idx) => {
                  const isSelected = filters.typeOfPurchase.includes(option);
                  return (
                    <button
                      key={option}
                      type="button"
                      onClick={() => toggleTypeOfPurchase(option)}
                      className={`px-2.5 py-1 text-xs font-medium transition-colors border ${
                        idx === 0 ? 'rounded-l' : ''
                      } ${
                        idx === TYPE_OF_PURCHASE_OPTIONS.length - 1 ? 'rounded-r' : ''
                      } ${
                        idx !== 0 ? '-ml-px' : ''
                      } ${
                        isSelected
                          ? 'bg-[#0488B6] text-white border-[#0488B6] z-10'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="h-4 w-px bg-gray-300 hidden sm:block"></div>

            <button
              type="button"
              onClick={handleVesselDepartureFilter}
              className={`px-2.5 py-1 text-xs font-medium rounded transition-colors border flex items-center gap-1.5 shadow-sm ${
                filters.showWithoutVesselDeparture
                  ? 'bg-orange-500 text-white border-orange-500 hover:bg-orange-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
              title="Show only records without vessel departure date"
            >
              <span className={`w-1.5 h-1.5 rounded-full ${
                filters.showWithoutVesselDeparture ? 'bg-white' : 'bg-orange-400'
              }`}></span>
              <span>No Vessel Departure</span>
            </button>

            {isFySelected && (
              <span className="inline-flex items-center gap-1.5 text-[11px] text-blue-800 bg-blue-50 px-2.5 py-1 rounded border border-blue-200 font-medium">
                <FaInfoCircle className="text-[#0488B6] text-xs flex-shrink-0" />
                <span>FY {filters.financialYear} Active: Applied only on Purchase Contracts</span>
              </span>
            )}
          </div>
          
          {/* Right: Search & Reset Buttons */}
          <div className="flex items-center gap-2 self-end md:self-auto">
            <button
              type="button"
              onClick={handleSearch}
              className="px-3.5 py-1 text-xs sm:text-sm font-medium bg-[#0488B6] text-white rounded hover:bg-[#0373a3] transition-colors flex items-center justify-center shadow-sm"
            >
              <FaSearch className="mr-1.5 text-xs" />
              <span>Search</span>
            </button>
            <button
              type="button"
              onClick={handleResetFilters}
              className="px-3 py-1 text-xs sm:text-sm font-medium bg-white text-gray-700 border border-gray-300 rounded hover:bg-gray-100 transition-colors shadow-sm"
            >
              Reset Filters
            </button>
          </div>
        </div>
      </div>


      {loading ? (
        <div className="flex flex-col items-center justify-center py-8 sm:py-12 space-y-3">
          <div className="animate-spin rounded-full h-8 w-8 sm:h-10 sm:w-10 border-t-2 border-b-2 border-[#0488B6]"></div>
          <p className="text-xs sm:text-sm text-gray-600 text-center">Loading report data...</p>
        </div>
      ) : groupedData.length > 0 ? (
        <div className="space-y-6">
          {groupedData.map((group, groupIndex) => {
            // Handle currency total rows - display as a summary card
            if (group.isCurrencyTotal) {
              return (
                <div
                  key={`currency-total-${groupIndex}`}
                  className="border-2 border-blue-300 rounded-lg overflow-hidden bg-gradient-to-r from-blue-50 to-indigo-50"
                >
                  <div className="px-4 sm:px-6 py-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
                      <div className="text-gray-800">
                        <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0">
                          <span className={`ml-0 sm:ml-3 px-4 py-2 rounded-full text-base font-bold ${
                            group.currency === 'USD' 
                              ? 'bg-green-200 text-green-900 border-2 border-green-400' 
                              : 'bg-orange-200 text-orange-900 border-2 border-orange-400'
                          }`}>
                            {group.currency} GRAND TOTAL
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 sm:gap-4">
                      <div className="bg-white p-3 rounded-lg border border-gray-300">
                        <div className="text-sm sm:text-base font-semibold text-gray-900 text-center">
                          {formatIndianNumber(group.currencyTotals.total_qty_mt)}
                        </div>
                        <div className="text-xs text-gray-600 text-center mt-1">Contracted Qty (MT)</div>
                      </div>
                      <div className="bg-white p-3 rounded-lg border border-gray-300">
                        <div className="text-sm sm:text-base font-semibold text-gray-900 text-center">
                          {group.currency === 'INR'
                            ? '₹' + formatIndianNumber(group.currencyTotals.total_amount_inr)
                            : '$' + formatIndianNumber(group.currencyTotals.total_amount_usd)}
                        </div>
                        <div className="text-xs text-gray-600 text-center mt-1">
                          Contract Amount ({group.currency === 'INR' ? '₹' : '$'})
                        </div>
                      </div>
                      <div className="bg-white p-3 rounded-lg border border-gray-300">
                        <div className="text-sm sm:text-base font-semibold text-gray-900 text-center">
                          {formatIndianNumber(group.currencyTotals.total_amount_inr)}
                        </div>
                        <div className="text-xs text-gray-600 text-center mt-1">Contract Amount (₹)</div>
                      </div>
                      <div className="bg-white p-3 rounded-lg border border-gray-300">
                        <div className="text-sm sm:text-base font-semibold text-gray-900 text-center">
                          {formatIndianNumber(group.currencyTotals.total_sale_qty)}
                        </div>
                        <div className="text-xs text-gray-600 text-center mt-1">Sale Qty (MT)</div>
                      </div>
                      <div className="bg-white p-3 rounded-lg border border-gray-300">
                        <div className="text-sm sm:text-base font-semibold text-gray-900 text-center">
                          {formatIndianNumber(group.currencyTotals.total_sale_amount)}
                        </div>
                        <div className="text-xs text-gray-600 text-center mt-1">Sale Amount (₹)</div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }

            // Regular group display
            return (
            <div
              key={groupIndex}
              className="border border-gray-200 rounded overflow-hidden "
            >
              {/* Group Header - Mobile Responsive */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-2 sm:space-y-0">
                  <div className="text-gray-800 text-sm">
                    <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0">
                      <span className="text-base sm:text-lg font-semibold">
                        {group.commodity}
                      </span>
                      <span className="text-base sm:text-lg font-semibold sm:ml-4">
                        State: {group.state}
                      </span>
                       <span className={`ml-0 sm:ml-3 px-3 py-1 rounded-full text-sm font-bold ${
              group.currency === 'USD' 
                ? 'bg-green-100 text-green-800 border border-green-300' 
                : 'bg-orange-100 text-orange-800 border border-orange-300'
            }`}>
              {group.currency}
            </span>
                    </div>
                  </div>
                  <div className="text-left sm:text-right">
                    <div className="text-xs sm:text-sm text-gray-600 space-y-1 sm:space-y-0">
                      <div className="sm:inline">
                        <span className="font-medium">Purchase:</span>{" "}
                        {formatIndianNumber(group.purchaseTotals.total_qty_mt)} MT
                      </div>
                      <div className="sm:inline sm:ml-2">
                        <span className="font-medium">Sales:</span>{" "}
                        {formatIndianNumber(group.salesTotals.total_qty_mt)} MT
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Group Content - Mobile Responsive Table */}
              <div className="w-full">
                {group.data.length > 0 ? (
                  <div className="relative">
                    {/* Mobile Table Container */}
                    <div
                      className="overflow-x-auto overflow-y-auto max-h-96 border border-gray-200 rounded"
                      style={{
                        scrollbarWidth: "thin",
                        scrollbarColor: "#cbd5e0 #f7fafc",
                      }}
                    >
                      <style jsx>{`
                        div::-webkit-scrollbar {
                          width: 6px;
                          height: 6px;
                        }
                        div::-webkit-scrollbar-track {
                          background: #f7fafc;
                          border-radius: 3px;
                        }
                        div::-webkit-scrollbar-thumb {
                          background: #cbd5e0;
                          border-radius: 3px;
                          cursor: pointer;
                        }
                        div::-webkit-scrollbar-thumb:hover {
                          background: #a0aec0;
                        }
                        div::-webkit-scrollbar-corner {
                          background: #f7fafc;
                        }
                      `}</style>
                      <table className="min-w-full divide-y divide-gray-200 text-xs sm:text-sm">
                      <thead className="bg-gray-50 text-gray-700 font-semibold sticky top-0 z-10">
                        <tr>
                          <th className="px-1 sm:px-2 py-1 sm:py-1.5 text-left whitespace-nowrap border-r border-gray-200 min-w-[40px]">
                            <span className="hidden sm:inline">Sl. No</span>
                            <span className="sm:hidden">#</span>
                          </th>
                          <th className="px-1 sm:px-2 py-1 sm:py-1.5 text-left whitespace-nowrap border-r border-gray-200 min-w-[120px]">
                            <span className="hidden lg:inline">Purchase Contract No</span>
                            <span className="lg:hidden hidden sm:inline">Contract No</span>
                            <span className="sm:hidden">Contract</span>
                          </th>
                          <th className="px-1 sm:px-2 py-1 sm:py-1.5 text-left whitespace-nowrap border-r border-gray-200 min-w-[80px]">
                            <span className="hidden sm:inline">Contract Date</span>
                            <span className="sm:hidden">Date</span>
                          </th>
                          <th className="px-1 sm:px-2 py-1 sm:py-1.5 text-left whitespace-nowrap border-r border-gray-200 min-w-[80px] hidden lg:table-cell">
                            <span className="hidden lg:inline">Commodity</span>
                          </th>
                          <th className="px-1 sm:px-2 py-1 sm:py-1.5 text-left whitespace-nowrap border-r border-gray-200 min-w-[100px]">
                            <span className="hidden lg:inline">Seller's Name</span>
                            <span className="lg:hidden hidden sm:inline">Seller</span>
                            <span className="sm:hidden">Seller</span>
                          </th>
                          <th className="px-1 sm:px-2 py-1 sm:py-1.5 text-left whitespace-nowrap border-r border-gray-200 min-w-[80px] hidden md:table-cell">
                            <span className="hidden lg:inline">Shipment Period</span>
                            <span className="lg:hidden">Period</span>
                          </th>
                          <th className="px-1 sm:px-2 py-1 sm:py-1.5 text-left whitespace-nowrap border-r border-gray-200 min-w-[80px] hidden lg:table-cell">
                            <span className="hidden lg:inline">Vessel's Name</span>
                            <span className="lg:hidden">Vessel</span>
                          </th>
                          <th className="px-1 sm:px-2 py-1 sm:py-1.5 text-left whitespace-nowrap border-r border-gray-200 min-w-[80px] hidden lg:table-cell">
                            <span className="hidden lg:inline">Port of Loading</span>
                            <span className="lg:hidden">Loading</span>
                          </th>
                          <th className="px-1 sm:px-2 py-1 sm:py-1.5 text-left whitespace-nowrap border-r border-gray-200 min-w-[80px] hidden lg:table-cell">
                            <span className="hidden lg:inline">Port of Discharge</span>
                            <span className="lg:hidden">Discharge</span>
                          </th>
                          <th className="px-1 sm:px-2 py-1 sm:py-1.5 text-right whitespace-nowrap border-r border-gray-200 min-w-[80px]">
                            <span className="hidden sm:inline">Contracted Qty (MT)</span>
                            <span className="sm:hidden">Qty</span>
                          </th>
                          <th className="px-1 sm:px-2 py-1 sm:py-1.5 whitespace-nowrap border-r border-gray-200 min-w-[100px] hidden lg:table-cell">
                            <span className="hidden lg:inline">Type of Purchase</span>
                            <span className="lg:hidden">Type</span>
                          </th>
                          <th className="px-1 sm:px-2 py-1 sm:py-1.5 whitespace-nowrap border-r border-gray-200 min-w-[80px] hidden md:table-cell">
                            <span className="hidden lg:inline">Currency</span>
                            <span className="lg:hidden">Curr</span>
                          </th>
                          <th className="px-1 sm:px-2 py-1 sm:py-1.5 text-right whitespace-nowrap border-r border-gray-200 min-w-[70px] hidden md:table-cell">
                            <span className="hidden lg:inline">Price (PMT)</span>
                            <span className="lg:hidden">Price</span>
                          </th>
                          <th className="px-1 sm:px-2 py-1 sm:py-1.5 text-right whitespace-nowrap border-r border-gray-200 min-w-[100px] hidden lg:table-cell">
                            <span className="hidden lg:inline">
                              Contract Amount ({group.currency === 'INR' ? '₹' : '$'})
                            </span>
                            <span className="lg:hidden">
                              Amount {group.currency === 'INR' ? '₹' : '$'}
                            </span>
                          </th>
                          <th className="px-1 sm:px-2 py-1 sm:py-1.5 text-right whitespace-nowrap border-r border-gray-200 min-w-[70px] hidden lg:table-cell">
                            <span className="hidden lg:inline">Exchange Rate</span>
                            <span className="lg:hidden">Ex Rate</span>
                          </th>
                          <th className="px-1 sm:px-2 py-1 sm:py-1.5 text-center whitespace-nowrap border-r border-gray-200 min-w-[70px] hidden lg:table-cell">
                            <span className="hidden lg:inline">Hedge Executed</span>
                            <span className="lg:hidden">Hedge</span>
                          </th>
                          <th className="px-1 sm:px-2 py-1 sm:py-1.5 text-right whitespace-nowrap border-r border-gray-200 min-w-[100px] hidden lg:table-cell">
                            <span className="hidden lg:inline">Contract Amount INR</span>
                            <span className="lg:hidden">Amount INR</span>
                          </th>
                          <th className="px-1 sm:px-2 py-1 sm:py-1.5 text-left whitespace-nowrap border-r border-gray-200 min-w-[80px] hidden lg:table-cell">
                            <span className="hidden lg:inline">Vessel Departure</span>
                            <span className="lg:hidden">Departure</span>
                          </th>
                          <th className="px-1 sm:px-2 py-1 sm:py-1.5 text-left whitespace-nowrap border-r border-gray-200 min-w-[80px] hidden lg:table-cell">
                            <span className="hidden lg:inline">ETA of Vessel</span>
                            <span className="lg:hidden">ETA</span>
                          </th>
                          <th className="px-1 sm:px-2 py-1 sm:py-1.5 text-left whitespace-nowrap border-l-2 border-gray-400 min-w-[80px]">
                            <span className="hidden sm:inline">Date of Sale</span>
                            <span className="sm:hidden">Sale Date</span>
                          </th>
                          <th className="px-1 sm:px-2 py-1 sm:py-1.5 text-left whitespace-nowrap border-r border-gray-200 min-w-[100px] hidden md:table-cell">
                            <span className="hidden lg:inline">Name of Party</span>
                            <span className="lg:hidden">Party</span>
                          </th>
                          <th className="px-1 sm:px-2 py-1 sm:py-1.5 text-right whitespace-nowrap border-r border-gray-200 min-w-[60px]">
                            <span className="hidden sm:inline">Qty (MT)</span>
                            <span className="sm:hidden">Qty</span>
                          </th>
                          <th className="px-1 sm:px-2 py-1 sm:py-1.5 text-right whitespace-nowrap border-r border-gray-200 min-w-[70px] hidden md:table-cell">
                            <span className="hidden lg:inline">Price PMT</span>
                            <span className="lg:hidden">Price</span>
                          </th>
                          <th className="px-1 sm:px-2 py-1 sm:py-1.5 text-right whitespace-nowrap min-w-[80px]">
                            <span className="hidden sm:inline">Amount</span>
                            <span className="sm:hidden">Amt</span>
                          </th>
                        </tr>
                      </thead>
                     <tbody className="bg-white divide-y divide-gray-100">
  {group.data.map((row, index) => {
    // Currency total row rendering
    if (row.isCurrencyTotal) {
      return (
        <tr key={`currency-total-${index}`} className="bg-blue-100 font-bold text-blue-900">
          <td className="px-1 sm:px-2 py-1 sm:py-1.5 border-r border-gray-200" colSpan="9">
            <span className="font-bold">TOTAL ({row.currency})</span>
          </td>
          <td className="px-1 sm:px-2 py-1 sm:py-1.5 text-right border-r border-gray-200">
            {formatIndianNumber(row.currencyTotals.total_qty_mt)}
          </td>
          <td className="px-1 sm:px-2 py-1 sm:py-1.5 border-r border-gray-200 hidden lg:table-cell">-</td>
          <td className="px-1 sm:px-2 py-1 sm:py-1.5 border-r border-gray-200 hidden md:table-cell">{row.currency}</td>
          <td className="px-1 sm:px-2 py-1 sm:py-1.5 text-right border-r border-gray-200 hidden md:table-cell">
            {row.currencyTotals.total_qty_mt > 0
              ? (row.currency === 'USD' ? '$' : '₹') + formatIndianNumber(row.currencyTotals.total_amount_usd / row.currencyTotals.total_qty_mt)
              : "-"}
          </td>
          <td className="px-1 sm:px-2 py-1 sm:py-1.5 text-right border-r border-gray-200 hidden lg:table-cell">
            {formatIndianNumber(row.currencyTotals.total_amount_usd)}
          </td>
          <td className="px-1 sm:px-2 py-1 sm:py-1.5 text-right border-r border-gray-200 hidden lg:table-cell">
            {row.currencyTotals.total_amount_usd > 0
              ? formatIndianNumber(row.currencyTotals.total_amount_inr / row.currencyTotals.total_amount_usd)
              : "-"}
          </td>
          <td className="px-1 sm:px-2 py-1 sm:py-1.5 text-center border-r border-gray-200 hidden lg:table-cell">-</td>
          <td className="px-1 sm:px-2 py-1 sm:py-1.5 text-right border-r border-gray-200 hidden lg:table-cell">
            {formatIndianNumber(row.currencyTotals.total_amount_inr)}
          </td>
          <td className="px-1 sm:px-2 py-1 sm:py-1.5 border-r border-gray-200 hidden lg:table-cell" colSpan="2">-</td>
          <td className="px-1 sm:px-2 py-1 sm:py-1.5 border-r border-gray-200 hidden md:table-cell" colSpan="2">-</td>
          <td className="px-1 sm:px-2 py-1 sm:py-1.5 text-right border-r border-gray-200">-</td>
          <td className="px-1 sm:px-2 py-1 sm:py-1.5 text-right border-r border-gray-200 hidden md:table-cell">-</td>
          <td className="px-1 sm:px-2 py-1 sm:py-1.5 text-right">-</td>
        </tr>
      );
    }

    // Subtotal row rendering
    if (row.isSubtotalRow) {
          const avgPrice = row.salesSubtotal.total_qty_mt > 0 
        ? row.salesSubtotal.total_amount / row.salesSubtotal.total_qty_mt 
        : 0;
      return (
        <tr key={`subtotal-${index}`} className="bg-blue-50 font-semibold border-t-2 border-blue-300">
          {/* Colspan should cover all purchase columns + Date of Sale + Name of Party = 19 columns */}
          <td colSpan="19" className="px-1 sm:px-2 py-1 sm:py-1.5 text-right text-xs sm:text-sm border-r border-gray-200">
            <span className="mr-4">Subtotal for {row.purchase_contract_no}:</span>
          </td>
          {/* Sale Qty MT */}
          <td className="px-1 sm:px-2 py-1 sm:py-1.5 text-right whitespace-nowrap border-r border-gray-200 text-xs sm:text-sm">
            {formatIndianNumber(row.salesSubtotal.total_qty_mt)}
          </td>
          {/* Price PMT - Empty */}
           <td className="px-1 sm:px-2 py-1 sm:py-1.5 text-right whitespace-nowrap border-r border-gray-200 text-xs sm:text-sm hidden md:table-cell">
            {avgPrice > 0 ? formatIndianNumber(avgPrice) : "-"}
          </td>
          {/* Sale Amount */}
          <td className="px-1 sm:px-2 py-1 sm:py-1.5 text-right whitespace-nowrap text-xs sm:text-sm">
            {formatIndianNumber(row.salesSubtotal.total_amount)}
          </td>
        </tr>
      );
    }

    // Regular data row
    return (
      <tr
        key={`row-${index}`}
        className={`hover:bg-blue-50 transition-colors duration-150 ${
          !row.isFirstRow ? "bg-gray-50" : ""
        }`}
      >
        <td className="px-1 sm:px-2 py-1 sm:py-1.5 whitespace-nowrap border-r border-gray-100 text-xs sm:text-sm">
          {row.sl_no}
        </td>
        <td className="px-1 sm:px-2 py-1 sm:py-1.5 whitespace-nowrap font-medium border-r border-gray-100 text-xs sm:text-sm">
          {row.isFirstRow ? row.purchase_contract_no || "-" : "---"}
        </td>
        <td className="px-1 sm:px-2 py-1 sm:py-1.5 whitespace-nowrap border-r border-gray-100 text-xs sm:text-sm">
          {row.isFirstRow ? formatDate(row.contract_date) : "---"}
        </td>
        <td className="px-1 sm:px-2 py-1 sm:py-1.5 whitespace-nowrap border-r border-gray-100 text-xs sm:text-sm hidden lg:table-cell">
          {row.isFirstRow ? row.commodity || "-" : "---"}
        </td>
        <td className="px-1 sm:px-2 py-1 sm:py-1.5 whitespace-nowrap border-r border-gray-100 text-xs sm:text-sm">
          {row.isFirstRow ? row.seller_name || "-" : "---"}
        </td>
        <td className="px-1 sm:px-2 py-1 sm:py-1.5 whitespace-nowrap border-r border-gray-100 text-xs sm:text-sm hidden md:table-cell">
          {row.isFirstRow ? formatDate(row.shipment_period) : "---"}
        </td>
        <td className="px-1 sm:px-2 py-1 sm:py-1.5 whitespace-nowrap border-r border-gray-100 text-xs sm:text-sm hidden lg:table-cell">
          {row.isFirstRow ? (row.vessel_name || "-") : "---"}
        </td>
        <td className="px-1 sm:px-2 py-1 sm:py-1.5 whitespace-nowrap border-r border-gray-100 text-xs sm:text-sm hidden lg:table-cell">
          {row.isFirstRow ? (row.port_of_loading || "-") : "---"}
        </td>
        <td className="px-1 sm:px-2 py-1 sm:py-1.5 whitespace-nowrap border-r border-gray-100 text-xs sm:text-sm hidden lg:table-cell">
          {row.isFirstRow ? (row.port_of_discharge || "-") : "---"}
        </td>
        <td className="px-1 sm:px-2 py-1 sm:py-1.5 text-right whitespace-nowrap border-r border-gray-100 text-xs sm:text-sm">
          {row.isFirstRow
            ? row.contracted_qty_mt
              ? formatIndianNumber(row.contracted_qty_mt)
              : "-"
            : "---"}
        </td>
        <td className="px-1 sm:px-2 py-1 sm:py-1.5 whitespace-nowrap border-r border-gray-100 text-xs sm:text-sm hidden lg:table-cell">
          {row.isFirstRow ? (row.type_of_purchase || "-") : "---"}
        </td>
        <td className="px-1 sm:px-2 py-1 sm:py-1.5 whitespace-nowrap border-r border-gray-100 text-xs sm:text-sm hidden md:table-cell">
          {row.isFirstRow ? (row.currency || "USD") : "---"}
        </td>
        <td className="px-1 sm:px-2 py-1 sm:py-1.5 text-right whitespace-nowrap border-r border-gray-100 text-xs sm:text-sm hidden md:table-cell">
          {row.isFirstRow
            ? row.price_usd_mt
              ? (row.currency === 'USD' ? '$' : '₹') + formatIndianNumber(row.price_usd_mt)
              : "-"
            : "---"}
        </td>
        <td className="px-1 sm:px-2 py-1 sm:py-1.5 text-right whitespace-nowrap border-r border-gray-100 text-xs sm:text-sm hidden lg:table-cell">
          {row.isFirstRow
            ? (() => {
                if (!row.contract_amount_usd) return "-";
                if (row.currency === 'INR') {
                  const contractAmountUsd = parseFloat(row.contract_amount_usd) || 0;
                  const hedgingRate = parseFloat(row.hedging_rate) || 0;
                  const contractAmountInr = contractAmountUsd * hedgingRate;
                  return contractAmountInr > 0 ? '₹' + formatIndianNumber(contractAmountInr) : "-";
                } else {
                  return '$' + formatIndianNumber(row.contract_amount_usd);
                }
              })()
            : "---"}
        </td>
        <td className="px-1 sm:px-2 py-1 sm:py-1.5 text-right whitespace-nowrap border-r border-gray-100 text-xs sm:text-sm hidden lg:table-cell">
          {row.isFirstRow
            ? (row.currency === 'INR' ? "1.00" : (row.hedging_rate ? formatIndianNumber(row.hedging_rate) : "-"))
            : "---"}
        </td>
        <td className="px-1 sm:px-2 py-1 sm:py-1.5 text-center whitespace-nowrap border-r border-gray-100 text-xs sm:text-sm hidden lg:table-cell">
          {row.isFirstRow
            ? (row.hedge_executed || "-")
            : "---"}
        </td>
        <td className="px-1 sm:px-2 py-1 sm:py-1.5 text-right whitespace-nowrap border-r border-gray-100 text-xs sm:text-sm hidden lg:table-cell">
          {row.isFirstRow
            ? (() => {
                const contractAmountUsd = parseFloat(row.contract_amount_usd) || 0;
                const hedgingRate = parseFloat(row.hedging_rate) || 0;
                const calculatedInr = contractAmountUsd * hedgingRate;
                return calculatedInr > 0 ? formatIndianNumber(calculatedInr) : "-";
              })()
            : "---"}
        </td>
        <td className="px-1 sm:px-2 py-1 sm:py-1.5 whitespace-nowrap border-r border-gray-100 text-xs sm:text-sm hidden lg:table-cell">
          {row.isFirstRow ? (row.vessel_departure_pod || "-") : "---"}
        </td>
        <td className="px-1 sm:px-2 py-1 sm:py-1.5 whitespace-nowrap border-r border-gray-100 text-xs sm:text-sm hidden lg:table-cell">
          {row.isFirstRow ? (row.eta_of_vessel || "-") : "---"}
        </td>
        {/* Hide Date of Sale - Always show "-" */}
        <td className="px-1 sm:px-2 py-1 sm:py-1.5 whitespace-nowrap border-l-2 border-gray-400 font-medium text-xs sm:text-sm">
          -
        </td>
        {/* Hide Name of Party - Always show "-" */}
        <td className="px-1 sm:px-2 py-1 sm:py-1.5 whitespace-nowrap border-r border-gray-100 text-xs sm:text-sm hidden md:table-cell">
          -
        </td>
        <td className="px-1 sm:px-2 py-1 sm:py-1.5 text-right whitespace-nowrap border-r border-gray-100 text-xs sm:text-sm">
          {row.sale_quantity_mt ? formatIndianNumber(row.sale_quantity_mt) : "-"}
        </td>
        <td className="px-1 sm:px-2 py-1 sm:py-1.5 text-right whitespace-nowrap border-r border-gray-100 text-xs sm:text-sm hidden md:table-cell">
          {row.price_pmt ? formatIndianNumber(row.price_pmt) : "-"}
        </td>
        <td className="px-1 sm:px-2 py-1 sm:py-1.5 text-right whitespace-nowrap text-xs sm:text-sm">
          {row.contracted_amount ? formatIndianNumber(row.contracted_amount) : "-"}
        </td>
      </tr>
    );
  })}
</tbody>

                      <tfoot className="bg-gray-100">
                        <tr className="font-semibold text-xs sm:text-sm">
                          <td
                            colSpan="9"
                            className="px-1 sm:px-2 py-1 sm:py-1.5 border-r border-gray-200"
                          >
                            <span className="hidden sm:inline">TOTAL</span>
                            <span className="sm:hidden">TOT</span>
                          </td>
                          <td className="px-1 sm:px-2 py-1 sm:py-1.5 text-right border-r border-gray-200">
                            {formatIndianNumber(
                              group.purchaseTotals.total_qty_mt
                            )}
                          </td>
                          <td className="px-1 sm:px-2 py-1 sm:py-1.5 border-r border-gray-200 hidden lg:table-cell">-</td>
                          <td className="px-1 sm:px-2 py-1 sm:py-1.5 border-r border-gray-200 hidden md:table-cell">-</td>
                          <td className="px-1 sm:px-2 py-1 sm:py-1.5 text-right border-r border-gray-200 hidden md:table-cell">
                            {formatIndianNumber(
                              group.purchaseTotals.avg_price_usd_mt
                            )}
                          </td>
                          <td className="px-1 sm:px-2 py-1 sm:py-1.5 text-right border-r border-gray-200 hidden lg:table-cell">
                            {group.currency === 'INR'
                              ? '₹' + formatIndianNumber(group.purchaseTotals.total_amount_inr)
                              : '$' + formatIndianNumber(group.purchaseTotals.total_amount_usd)}
                          </td>
                          <td className="px-1 sm:px-2 py-1 sm:py-1.5 text-right border-r border-gray-200 hidden lg:table-cell">
                            {group.currency === 'INR' ? "1.00" : formatIndianNumber(
                              group.purchaseTotals.total_amount_inr /
                                (group.purchaseTotals.total_amount_usd || 1)
                            )}
                          </td>
                          <td className="px-1 sm:px-2 py-1 sm:py-1.5 text-center border-r border-gray-200 hidden lg:table-cell">-</td>
                          <td className="px-1 sm:px-2 py-1 sm:py-1.5 text-right border-r border-gray-200 hidden lg:table-cell">
                            {formatIndianNumber(
                              group.purchaseTotals.total_amount_inr
                            )}
                          </td>
                          <td
                            colSpan="2"
                            className="border-r border-gray-200 hidden lg:table-cell"
                          ></td>
                          <td
                            colSpan="2"
                            className="px-1 sm:px-2 py-1 sm:py-1.5 border-l-2 border-gray-400 hidden md:table-cell"
                          ></td>
                          <td className="px-1 sm:px-2 py-1 sm:py-1.5 text-right border-r border-gray-200">
                            {formatIndianNumber(group.salesTotals.total_qty_mt)}
                          </td>
                          <td className="px-1 sm:px-2 py-1 sm:py-1.5 text-right border-r border-gray-200 hidden md:table-cell">
                            {formatIndianNumber(
                              group.salesTotals.avg_price_pmt
                            )}
                          </td>
                          <td className="px-1 sm:px-2 py-1 sm:py-1.5 text-right">
                            {formatIndianNumber(group.salesTotals.total_amount)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                    </div>
                ) : (
                  <div className="p-3 sm:p-4">
                    <NoRecordFound message="No data available" />
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8 sm:py-12">
          <NoRecordFound message="No data available for the selected filters" />
        </div>
      )}
    </div>
  );
};

export default ImportSheetSummary;
